/**
 * The reader's text model. A library document arrives as extracted text with
 * ragged whitespace; the reader shows it as paragraphs and finds within it.
 * `flat` is the paragraphs joined by one space each, so a range in `flat` is
 * a range in the rendered text too (paragraph `i` starts at `starts[i]`).
 */
export type ReaderText = {
  paragraphs: string[];
  starts: number[];
  flat: string;
};

export type Range = [start: number, end: number];

const collapse = (text: string) => text.replace(/\s+/g, " ").trim();

export const toReaderText = (raw: string): ReaderText => {
  let blocks = raw.split(/\n[ \t]*\n/);
  // Statutes and manuals often come with no blank lines; their lines are the blocks.
  if (blocks.length < 3 && raw.length > 2000) blocks = raw.split(/\n/);
  const paragraphs = blocks.map(collapse).filter(Boolean);
  const starts: number[] = [];
  let at = 0;
  for (const paragraph of paragraphs) {
    starts.push(at);
    at += paragraph.length + 1;
  }
  return { paragraphs, starts, flat: paragraphs.join(" ") };
};

/** Where `term` occurs in `flat`, case-insensitively, at most `cap` times. */
export const findAll = (flat: string, term: string, cap = 500): Range[] => {
  const needle = collapse(term).toLowerCase();
  if (needle.length < 2) return [];
  const hay = flat.toLowerCase();
  const out: Range[] = [];
  let from = 0;
  while (out.length < cap) {
    const at = hay.indexOf(needle, from);
    if (at < 0) break;
    out.push([at, at + needle.length]);
    from = at + needle.length;
  }
  return out;
};

/**
 * Where a passage the library returned sits in the reader's text. Offsets
 * from the library count the raw text, so the passage is found by its words.
 */
export const locate = (flat: string, passage: string): Range | null => {
  const text = collapse(passage);
  if (!text) return null;
  const hay = flat.toLowerCase();
  for (const size of [80, 40, 20]) {
    const probe = text.slice(0, size).toLowerCase();
    const at = hay.indexOf(probe);
    if (at >= 0) return [at, Math.min(flat.length, at + text.length)];
  }
  return null;
};

export type Segment = {
  text: string;
  mark?: "match" | "active" | "passage";
  /** The find match this part belongs to. */
  match?: number;
};

/**
 * Paragraph `index` split around the ranges that touch it. `active` is the
 * match the reader is on; `passage` is the passage it jumped to.
 */
export const segmentsOf = (
  reader: ReaderText,
  index: number,
  matches: Range[],
  active: number,
  passage: Range | null
): Segment[] => {
  const paragraph = reader.paragraphs[index];
  const base = reader.starts[index];
  const end = base + paragraph.length;
  type Mark = {
    range: Range;
    mark: NonNullable<Segment["mark"]>;
    match: number;
  };
  const marks: Mark[] = [];
  if (passage && passage[0] < end && passage[1] > base)
    marks.push({ range: passage, mark: "passage", match: -1 });
  matches.forEach((range, i) => {
    if (range[0] < end && range[1] > base)
      marks.push({ range, mark: i === active ? "active" : "match", match: i });
  });
  if (!marks.length) return [{ text: paragraph }];
  // A match inside the passage wins over the passage tint.
  const points = new Set<number>([0, paragraph.length]);
  for (const { range } of marks) {
    points.add(Math.max(0, range[0] - base));
    points.add(Math.min(paragraph.length, range[1] - base));
  }
  const cuts = [...points].sort((a, b) => a - b);
  const segments: Segment[] = [];
  for (let i = 0; i < cuts.length - 1; i += 1) {
    const [from, to] = [cuts[i], cuts[i + 1]];
    const covering = marks.filter(
      ({ range }) => range[0] - base <= from && range[1] - base >= to
    );
    const match = covering.find((m) => m.mark !== "passage");
    const mark = match?.mark ?? covering[0]?.mark;
    segments.push({
      text: paragraph.slice(from, to),
      ...(mark ? { mark } : {}),
      ...(match ? { match: match.match } : {})
    });
  }
  return segments;
};

const STOP = new Set([
  "the",
  "and",
  "for",
  "with",
  "that",
  "from",
  "into",
  "this",
  "are",
  "was",
  "not"
]);

/** The words of a query worth highlighting in a snippet. */
export const queryTerms = (query: string): string[] =>
  [
    ...new Set(
      query
        .toLowerCase()
        .split(/[^\p{L}\p{N}-]+/u)
        .filter((w) => w.length >= 3 && !STOP.has(w))
    )
  ].slice(0, 8);

/** `text` split so each query term is its own marked part. */
export const highlightParts = (
  text: string,
  terms: string[]
): { text: string; hit: boolean }[] => {
  if (!terms.length || !text) return [{ text, hit: false }];
  const escaped = terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const pattern = new RegExp(`(${escaped.join("|")})`, "gi");
  return text
    .split(pattern)
    .filter(Boolean)
    .map((part) => ({ text: part, hit: terms.includes(part.toLowerCase()) }));
};
